import { Component, OnInit, ViewChild } from "@angular/core";
import { GraphApplication } from "src/app/models/graph/applications/GraphApplication";
import { GraphApplicationService } from "src/app/services/graph/GraphApplication.service";
import { PageLayout } from '@cvx/nextpage';
import { IGraphApplicationPermissions } from "src/app/models/graph/applications/GraphApplicationPermissions";
import { IWorkflowRequest } from "src/app/models/requests/WorkflowRequest";
import { MatTableDataSource } from "@angular/material/table";
import { SelectionModel } from "@angular/cdk/collections";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { lastValueFrom } from 'rxjs';
import { GraphApplicationRequestableApplicationPermission } from 'src/app/models/graph/applications/GraphApplicationRequestableApplicationPermission';
import { ApplicationRegistrationTenantType } from "src/app/models/ApplicationRegistrationTenantType";
import { IDirectoryUser } from "src/app/models/directory/users/DirectoryUser";
import { DialogDirectorySearchComponent } from "../../_shared/dialog-directory-search/dialog-directory-search.component";
import { MatDialog } from "@angular/material/dialog";
import { GroupMemberType } from "src/app/models/directory/enums/GroupMemberType.enum";
import { DirectoryDomain } from "src/app/models/directory/enums/DirectoryDomain.enum";
import { UserType } from "src/app/models/directory/enums/UserType.enum";
import { IDialogDirectoryData } from "src/app/models/components/IDialogDirectoryData";
import { NonChevronApplicationPermissionUpdate, NonChevronApplicationPermissionUpdateRequest } from "src/app/models/graph/applications/UpdateNonChevronApplicationPermissionRequest";
import { IGenericApiResponseWithWorkflowRequest } from "src/app/models/common/GenericApiResponseWithWorkflowRequest";
import { GraphUserService } from "src/app/services/graph/GraphUser.service";
import { CalAngularService } from "@cvx/cal-angular";
import { csvHelper } from "src/app/helpers/exporter/csvHelper";
import { appPermissionTypeToNumber } from "src/app/models/graph/applications/enums/AppPermissionType";
import { DateHelpers } from "src/app/helpers/dateHelpers";

@Component({
    selector: 'app-update-non-chevron',
    templateUrl: './update-non-chevron-permissions.component.html',
    styleUrls: ['./update-non-chevron-permissions.component.css']
})
export class UpdateNonChevronPermissionsComponent implements OnInit {

    @ViewChild(MatPaginator, { static: false })
    set paginator(value: MatPaginator) {
        this.requestablePermissionsDataSource.paginator = value;
    }
    @ViewChild(MatSort, { static: false })
    set sort(value: MatSort) {
        this.requestablePermissionsDataSource.sort = value;
    }

    ngOnInit()
    {
        this.requestablePermissionsDataSource.paginator = this.paginator
        this.requestablePermissionsDataSource.sort = this.sort
        this.requestablePermissionsDataSource.filterPredicate = (data: GraphApplicationRequestableApplicationPermission, filter: string) => {
            return data.permissionType.toLocaleLowerCase().includes(filter) || data.permissionValue.toLocaleLowerCase().includes(filter) || data.owners.map(o => o.displayName).join(' ').toLocaleLowerCase().includes(filter);
        }
    }

    
    applicationRegistrationType : ApplicationRegistrationTenantType = ApplicationRegistrationTenantType.NonChevron;
    PageLayout = PageLayout;
    getPermissionsErrorMessage: string;
    submitErrorMessage = { message: '', errors: [] };
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    isCompleted: boolean = false;
    clientApp?: GraphApplication;
    
    requestablePermissionsDataSource = new MatTableDataSource<GraphApplicationRequestableApplicationPermission>([]);
    selection = new SelectionModel<GraphApplicationRequestableApplicationPermission>(true, []);
    commonColumns: string[] = ['permissionType','permissionValue','owners','preapproved'];
    selectedPermissionsDisplayedColumns: string[] = [...this.commonColumns, 'del'];
    selectPermissionsDisplayedColumns: string[] = ['select', ...this.commonColumns, 'lastApprovedDate', 'lastApprovedBy'];
    allSelectedOwners: IDirectoryUser[] = [];
    displayClientApp: any;
    submittedRequests: IWorkflowRequest[] = [];

    constructor(
        private graphApplicationService: GraphApplicationService,
        private dialog: MatDialog,
        private graphUserService: GraphUserService,
        private authService: CalAngularService
    ) { }

    // used in the .html file
    public DateHelpers = DateHelpers;

    exportToCsv() {
        const columns = ["partitionKey", "rowKey", "resourceAppId", "appPermissionType", "preapproved", "birthright", "isActive", "birthrightWeb", "birthrightNative", "owner", "approvedBy", "lastApprovedDate", "name", "value"];

        const mappedData = this.requestablePermissionsDataSource.data.map(item => ({
            partitionKey: item.objectId,
            rowKey: item.permissionId,
            resourceAppId: item.appId,
            appPermissionType: appPermissionTypeToNumber(item.permissionType),
            preapproved: item.preapproved,
            birthright: item.isBirthrightNative && item.isBirthrightWeb,
            isActive: item.isActive,
            birthrightWeb: item.isBirthrightWeb,
            birthrightNative: item.isBirthrightNative,
            owner: item.owners.map(owner => owner.id).join(';'),
            approvedBy: item.lastApprovedBy? item.lastApprovedBy.id : '',
            lastApprovedDate: item.lastApproved,
            name: item.permissionDescription,
            value: item.permissionValue,
        }));

        const csvData = csvHelper.ConvertToCSV(mappedData, columns);
        csvHelper.DownloadFile(csvData, `export-nonChevronPermissions-${this.displayClientApp.appId}-${new Date().toISOString()}`);
    }

    clientAppSelected(data: IGraphApplicationPermissions) {

        this.clientApp = data.application;

        this.displayClientApp = {
            id: data.application.servicePrincipalId,
            appId: data.application.appId,
            name: data.application.displayName
        };
        this.getPermissions();
    }

    async getPermissions() : Promise<void>
    {
        this.selection.clear();
        this.requestablePermissionsDataSource.data = [];
        if(this.clientApp)
        {
            this.isLoading = true;
            await lastValueFrom(this.graphApplicationService.GetRequestablePermissions(this.clientApp.appId, true)).then((response) => {
                this.requestablePermissionsDataSource.data = response.data;
            }).catch(ex => {
                console.log(ex);
            })
            .finally(() => {
                this.isLoading = false;
            });
        }
    }

    isAllSelected() {
        return this.requestablePermissionsDataSource.filteredData.every(row => this.selection.selected.some(selected => selected.permissionId === row.permissionId));
    }

    masterTogglePermissions() {
        return this.isAllSelected() ?
            this.selection.clear() :
            this.requestablePermissionsDataSource.filteredData.forEach((row: any) => {
                if (!this.isSelected(row)) {
                    this.selectPermission(row);
                }
            });
    }

    selectPermission(row: GraphApplicationRequestableApplicationPermission) {
        // deep copy the row to avoid reference issues
        if (this.isSelected(row)) {
            this.deleteRow(row);
        }
        else {
            const copiedRow = JSON.parse(JSON.stringify(row));
            this.selection.toggle(copiedRow);
        }
        this.calculateAllSelectedOwners();
    }

    calculateAllSelectedOwners() {
        let allOwners = this.selection.selected.map(p => p.owners).flat();
        this.allSelectedOwners = [...new Map(allOwners.map(o => [o.id, o])).values()];
    }

    isSelected(row: GraphApplicationRequestableApplicationPermission) {
        return this.selection.selected.filter(p => p.permissionId === row.permissionId).length > 0;
    }
    
    deleteRow(selected: GraphApplicationRequestableApplicationPermission) {
        this.selection.setSelection(...this.selection.selected.filter(p => p.permissionId !== selected.permissionId));
        this.calculateAllSelectedOwners();
    }

    addOwner(permission: GraphApplicationRequestableApplicationPermission, id: string, firstName: string, lastName: string) {
        const selectedPermission = this.selection.selected.find(p => p.permissionId === permission.permissionId);
        if (selectedPermission && selectedPermission.owners.filter(owner => owner.id === id).length === 0) {
            selectedPermission.owners = [...selectedPermission.owners, { id: id, firstName: firstName, lastName: lastName } as IDirectoryUser];
        }
        
        this.calculateAllSelectedOwners();
    }

    removeOwner(permission: GraphApplicationRequestableApplicationPermission, id: string) {
        const selectedPermission = this.selection.selected.find(p => p.permissionId === permission.permissionId);
        if (selectedPermission) {
            selectedPermission.owners = selectedPermission.owners.filter(owner => owner.id !== id);
        }
        this.calculateAllSelectedOwners();
    }

    bulkRemoveOwner(id: string) {
        this.selection.selected.forEach(p => this.removeOwner(p, id));
    }

    searchAddPrimaryUser(permissionsToModify: GraphApplicationRequestableApplicationPermission[]): void {
        const dialogRef = this.dialog.open(DialogDirectorySearchComponent, {
            disableClose: true,
            autoFocus: true,
            maxWidth: 1000,
            width: '100%',
            data: {
                type: GroupMemberType.User,
                domain: DirectoryDomain.Chevron,
                userType: UserType.Primary,
                filterGroupRemoveNotAvailableAsGroupMember: false,
                filterGroupRemoveDynamicMembershipEnabled: false,
                filterGroupOnlyManagedByIdamp: false
            } as IDialogDirectoryData
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                permissionsToModify.forEach(p => this.addOwner(p, result.id, result.firstName, result.lastName));
            }
        });
    }

    isAllPreapprovedSelected() {
        return this.selection.selected.length === this.selection.selected.filter(p => p.preapproved).length;
    }
    masterTogglePreapproved() {
        if (this.isAllPreapprovedSelected()) {
            this.selection.selected.forEach(p => p.preapproved = false);
        }
        else {
            this.selection.selected.forEach(p => p.preapproved = true);
        }
    }
    togglePreapproved(permission: GraphApplicationRequestableApplicationPermission) {
        permission.preapproved = !permission.preapproved;
    }

    hasPermissions() {
        return this.graphUserService.CheckIsNonChevronWriter(this.authService.cvxClaimsPrincipal) || this.graphUserService.CheckIsNonChevronReader(this.authService.cvxClaimsPrincipal);
    }

    hasWritePermissions() {
        return this.graphUserService.CheckIsNonChevronWriter(this.authService.cvxClaimsPrincipal);
    }

    public doFilterPermissions = (value: any) => {
        this.requestablePermissionsDataSource.filter = value.target.value.trim().toLocaleLowerCase();
    }

    async submitRequest()
    {
        if (this.selection.selected.some(p => p.owners.length === 0)) {
            this.submitErrorMessage.message = 'All permissions must have at least one owner';
            return;
        }

        if (this.clientApp !== undefined && this.selection.selected.length > 0) 
            {
                this.isSubmitting = true;
                let payload = new NonChevronApplicationPermissionUpdateRequest(this.selection.selected.map(p => 
                    new NonChevronApplicationPermissionUpdate(p.permissionId, this.clientApp?.servicePrincipalId!, p.preapproved, p.owners.map(o => o.id))
                ));
    
                const observer = {
                    next: (x: IGenericApiResponseWithWorkflowRequest<any>) => {
                        this.submittedRequests = x.requests;
                    },
                    error: (err: any) => {
                        this.submitErrorMessage.message = err.statusText;
                        this.submitErrorMessage.errors = err?.error?.errors ?? [];
                        this.isSubmitting = false;
                    },
                    complete: () => {
                        this.isSubmitting = false;
                        this.isCompleted = true;
                        this.submitErrorMessage.message = "";
                        this.submitErrorMessage.errors = [];
                    }
                };
    
                this.isSubmitting = true;
                let submitCall = this.graphApplicationService.UpdateNonChevronApplicationPermission(payload);
                submitCall.subscribe(observer);
            }
    }

    resetForm() {
        this.clientApp = undefined;
        this.displayClientApp = undefined;
        this.getPermissionsErrorMessage = "";
        this.requestablePermissionsDataSource.data = [];
        this.selection.clear();
        this.submitErrorMessage.message = '';
        this.submitErrorMessage.errors = [];
    }
}
